/* Small controls the screens share: the segmented control, the title band every
 * destination opens with, the section panel,
 * the sortable heading and pager the two paged lists are built from, the date
 * and password fields, the modal
 * shell and the toast. Everything else is markup local to a screen. */
import { useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { PAGE_SIZES, type SortDirection } from '@oneshot2pac/shared';
import { formatDay, isDay, type IsoDay } from '@/domain/time';
import { NO_AUTOFILL } from './autofill';

export interface SegOption {
  label: string;
  on: boolean;
  pick: () => void;
}

/** Segmented control. The accent fill marks the live option and the label is
 *  always present — status is never carried by colour alone. It hugs its
 *  labels by default; `fill` spreads the options across the full width, which
 *  is what a form field wants so the pill lines up with the inputs beside it. */
export function Seg({
  options,
  height = 40,
  fill = false,
}: {
  options: SegOption[];
  height?: number;
  fill?: boolean;
}) {
  return (
    <div
      className="seg"
      style={{ height, ...(fill ? { display: 'flex', width: '100%' } : { alignSelf: 'start' }) }}
    >
      {options.map((o) => (
        <button
          key={o.label}
          type="button"
          className="seg-opt"
          aria-pressed={o.on}
          onClick={o.pick}
          style={{
            minHeight: 38,
            padding: '0 14px',
            ...(fill ? { flex: 1, justifyContent: 'center' } : null),
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            background: o.on ? 'var(--accent)' : 'transparent',
            color: o.on ? 'var(--on-accent)' : 'var(--ink)',
            fontFamily: 'var(--font-heading)',
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The band every destination opens with: the page's name, one line saying
 *  what is on screen, and the controls that change it — **one row**, wrapping
 *  onto a second only when the screen is narrower than that row measures.
 *
 *  One component rather than five copies of the same flex row, for the reason
 *  `SortHeader` is one: five copies is how one of them stops matching the other
 *  four, and the title's size, the band's padding and the gap down to the first
 *  panel are either identical on every screen or the app reads as five pages
 *  built by five people. 28px is what a destination is called — a sub-page (a
 *  customer, a job) is 26 and is not this component.
 *
 *  **Vertical padding only.** The sides are the page's gutter, which the screen
 *  supplies — 24px, the same as the brand and the destinations in the chrome
 *  above, so a title lines up with the nav over it and the card under it. A
 *  padding of its own here would sit *inside* that gutter and double it, which
 *  is exactly what happened the first time. The board is the one screen whose
 *  section cannot carry a gutter (its columns are flush to the edge and pad
 *  themselves at each card scale), so it wraps this in one.
 *
 *  `children` are spread into the row, not wrapped in anything. A wrapper would
 *  have to shrink before a control inside it could, and the searches lean on
 *  exactly that. Push the first of them over with `marginLeft: 'auto'`.
 *
 *  A control that should give up width rather than take a second line must be
 *  written `flex: '1 1 <floor>'` with a `maxWidth` of what it is drawn at —
 *  never `'0 1 <drawn>'`. Flex decides where a line breaks from the *basis*, so
 *  a basis of the full width wraps the row before it ever shrinks anything;
 *  the floor as the basis puts the break where the control stops being usable
 *  and lets it grow back into whatever room is left. */
export function PageHead({
  title,
  beside,
  caption,
  urgent = false,
  below,
  children,
}: {
  title: string;
  /** Something belonging to the title itself, not to the page — the schedule's ⓘ. */
  beside?: ReactNode;
  caption?: ReactNode;
  /** The caption is the only thing on this row that ever carries bad news. */
  urgent?: boolean;
  /** A full-width block under the row, inside the band. */
  below?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div style={{ flex: 'none', padding: '14px 0 12px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px 16px',
        }}
      >
        <h1 style={{ fontSize: 28, letterSpacing: '0.01em', margin: 0 }}>{title}</h1>
        {beside}
        {caption == null ? null : (
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: urgent ? 'var(--urgent)' : 'var(--ink-muted)',
            }}
          >
            {caption}
          </p>
        )}
        {children}
      </div>
      {below}
    </div>
  );
}

/** A hairline-bordered panel on the surface colour — the job detail, settings
 *  and customer screens are stacks of these. */
export function Panel({
  children,
  padding = 18,
  style,
}: {
  children: ReactNode;
  padding?: number | string;
  style?: CSSProperties;
}) {
  return (
    <div className="blueprint" style={{ background: 'var(--surface)', padding, ...style }}>
      {children}
    </div>
  );
}

/** A panel's heading row: uppercase Archivo title, mono metadata, optional
 *  action pushed right. */
export function PanelHead({
  title,
  meta,
  action,
}: {
  title: string;
  meta?: string;
  action?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
      <h2
        style={{
          fontSize: 15,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          margin: 0,
        }}
      >
        {title}
      </h2>
      {meta ? (
        <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--ink-muted)' }}>
          {meta}
        </span>
      ) : null}
      {action ? <span style={{ marginLeft: 'auto' }}>{action}</span> : null}
    </div>
  );
}

/** A tab group over sections of one screen, in the segmented control's own
 *  language. It exists because the job sheet is taller than an iPad: items,
 *  tasks and photos are three long lists that were stacked, and stacking them
 *  meant scrolling past two to reach the third.
 *
 *  Each tab carries its own count, so nothing is hidden by switching away —
 *  you can see there are nine tasks without opening them. Roles and arrow keys
 *  are real tabs, not buttons that look like tabs, and the panel points back at
 *  the tab that opened it. */
export interface TabOption {
  key: string;
  label: string;
  /** The tab's own headline number — "16 items", "2 of 9 done". */
  meta?: string;
}

export function Tabs({
  options,
  active,
  onPick,
  label,
  tabIdFor,
  panelId,
}: {
  options: TabOption[];
  active: string;
  onPick: (key: string) => void;
  label: string;
  /** The ids belong to the screen, which owns the panel below: it names the
   *  panel and the tabs, and both halves of the pairing stay in one file. */
  tabIdFor: (key: string) => string;
  panelId: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);

  // Left/right move between tabs and take focus with them; Home and End go to
  // the ends. That is what a tablist is expected to do, and it is the only way
  // through this panel on a keyboard.
  const step = (from: number, to: number) => {
    const next = (to + options.length) % options.length;
    if (next === from) return;
    onPick(options[next].key);
    wrap.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };

  return (
    <div
      className="seg"
      role="tablist"
      aria-label={label}
      ref={wrap}
      style={{ height: 48, flex: 'none' }}
    >
      {options.map((o, i) => {
        const on = o.key === active;
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            id={tabIdFor(o.key)}
            aria-selected={on}
            aria-controls={panelId}
            tabIndex={on ? 0 : -1}
            onClick={() => onPick(o.key)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') step(i, i - 1);
              else if (e.key === 'ArrowRight') step(i, i + 1);
              else if (e.key === 'Home') step(i, 0);
              else if (e.key === 'End') step(i, options.length - 1);
              else return;
              e.preventDefault();
            }}
            className="seg-opt"
            style={{
              minHeight: 46,
              padding: '0 16px',
              cursor: 'pointer',
              background: on ? 'var(--accent)' : 'transparent',
              color: on ? 'var(--on-accent)' : 'var(--ink)',
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: 11.5,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
            {o.meta ? (
              <span
                style={{
                  fontFamily: 'var(--font-data)',
                  fontSize: 11,
                  letterSpacing: 0,
                  textTransform: 'none',
                  color: on ? 'var(--on-accent)' : 'var(--ink-muted)',
                }}
              >
                {o.meta}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

/* — the two paged lists —
 *
 * The jobs list and the customers list are the same furniture around different
 * rows: headings you can order by, and a foot that says where in the list you
 * are. Both are here rather than in either screen, because two copies is how
 * one of them quietly stops matching the other. */

/** A column heading you can click to order by.
 *
 *  A button inside the `th`, not a click handler on the cell: it is an action,
 *  so it has to be reachable and operable from the keyboard, and it carries a
 *  focus ring of its own. `aria-sort` on the header is what tells a screen
 *  reader which column the table is in order of, and which way. */
export function SortHeader({
  heading,
  on,
  dir,
  onSort,
  title,
  align = 'left',
  style,
}: {
  heading: string;
  /** True when the list is in this column's order. */
  on: boolean;
  dir: SortDirection;
  onSort: () => void;
  /** What the click will do, not what it just did — the caption under the
   *  screen's heading already says what the list is in order of. */
  title: string;
  align?: 'left' | 'right';
  style?: CSSProperties;
}) {
  return (
    <th aria-sort={on ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'} style={style}>
      <button
        type="button"
        onClick={onSort}
        title={title}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          minHeight: 32,
          padding: '4px 6px',
          marginLeft: align === 'left' ? -6 : 0,
          marginRight: align === 'right' ? -6 : 0,
          border: 0,
          borderRadius: 6,
          background: 'transparent',
          cursor: 'pointer',
          font: 'inherit',
          letterSpacing: 'inherit',
          textTransform: 'inherit',
          color: on ? 'var(--ink)' : 'inherit',
        }}
      >
        {heading}
        {/* Shape and position, never colour on its own: a filled caret for the
            column in play, a faint pair of them for a column that could be. */}
        <span aria-hidden="true" style={{ fontSize: 9, lineHeight: 1, opacity: on ? 1 : 0.35 }}>
          {on ? (dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </button>
    </th>
  );
}

/** The foot of a paged list: what is on screen, how much of a page it is, and
 *  the way to the next one.
 *
 *  It rides at the bottom of the card rather than after the table, so it is on
 *  screen whatever the page holds — a pager you have to scroll a hundred rows
 *  to reach is a pager nobody finds.
 *
 *  Deliberately not a numbered row of pages: the office gets to a job or a
 *  customer by searching for it, and twelve page numbers is twelve 48px targets
 *  that all look the same.
 *
 *  The rows-per-page control is always here, and Previous/Next only when there
 *  is more than one page. That asymmetry is the point — hiding the size control
 *  on a single page is a trap, because setting 100 rows would be the last
 *  moment you could ever set it back to 25. */
export function Pager({
  page,
  pages,
  total,
  shown,
  size,
  busy = false,
  onPage,
  onSize,
  empty,
}: {
  page: number;
  pages: number;
  /** Every row matching the search, not just the ones on screen. */
  total: number;
  /** How many rows this page actually drew. */
  shown: number;
  size: number;
  /** A page on its way in: the steps are held rather than queued up. */
  busy?: boolean;
  onPage: (page: number) => void;
  onSize: (size: number) => void;
  /** What to say when the page drew nothing — "No jobs on this page". */
  empty: string;
}) {
  // Two pagers can be on one page, so the label the size options are grouped
  // under needs an id nobody else has.
  const rows = useId();
  const first = (page - 1) * size + 1;
  const strong = { color: 'var(--ink)', fontWeight: 500 } as const;

  return (
    <div
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        flexWrap: 'wrap',
        padding: '10px 16px',
        borderTop: '1px solid var(--line)',
        background: 'var(--surface)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--ink-muted)' }}>
        {shown === 0 ? (
          empty
        ) : (
          <>
            Showing <strong style={strong}>{first}</strong>–
            <strong style={strong}>{first + shown - 1}</strong> of {total}
          </>
        )}
      </span>

      {/* The options are bare numbers, so the group is what says what they
          count — "25, pressed" alone tells a screen reader nothing. */}
      <div
        role="group"
        aria-labelledby={rows}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
      >
        <span id={rows} style={{ fontSize: 13, color: 'var(--ink-muted)' }}>
          Rows per page
        </span>
        <Seg
          height={40}
          options={PAGE_SIZES.map((n) => ({
            label: String(n),
            on: size === n,
            pick: () => onSize(n),
          }))}
        />
      </div>

      {pages > 1 ? (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ height: 44 }}
            disabled={page <= 1 || busy}
            onClick={() => onPage(page - 1)}
          >
            ← Previous
          </button>
          <span
            style={{ fontSize: 13, color: 'var(--ink-muted)', minWidth: 92, textAlign: 'center' }}
          >
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ height: 44 }}
            disabled={page >= pages || busy}
            onClick={() => onPage(page + 1)}
          >
            Next →
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** A small uppercase field label. */
export function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="label-xs">{children}</span>;
}

/** A password field with a reveal toggle.
 *
 *  Every password in this app is typed by someone who cannot see what they are
 *  typing, in a shop where a glove or the glare is enough to mistype it. The
 *  eye turns the field into plain text so it can be read back, and the button
 *  says which way it is going rather than relying on the glyph.
 *
 *  `shown` sets which way it starts. Signing in and changing your own start
 *  hidden; the two the office types for somebody else start on screen, because
 *  the whole point of them is to be read out to the person standing there.
 */
export function PasswordField({
  label,
  name,
  value,
  onChange,
  hint,
  note,
  autoComplete = NO_AUTOFILL,
  autoFocus = false,
  required = false,
  shown: initiallyShown = false,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  note?: ReactNode;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  shown?: boolean;
}) {
  const id = useId();
  const [shown, setShown] = useState(initiallyShown);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label className="label-xs" htmlFor={id}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          className="input"
          id={id}
          name={name}
          type={shown ? 'text' : 'password'}
          value={value}
          placeholder={hint}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          required={required}
          onChange={(e) => onChange(e.target.value)}
          style={{ height: 48, fontSize: 16, paddingRight: 48 }}
        />
        <button
          type="button"
          onClick={() => setShown((on) => !on)}
          aria-label={shown ? 'Hide password' : 'Show password'}
          aria-pressed={shown}
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            background: 'none',
            border: 0,
            borderRadius: 10,
            color: 'var(--ink-muted)',
            cursor: 'pointer',
          }}
        >
          <EyeIcon crossed={shown} />
        </button>
      </div>
      {note === undefined ? null : (
        <span style={{ fontSize: 12, color: 'var(--ink-muted)', lineHeight: 1.45 }}>{note}</span>
      )}
    </div>
  );
}

/** The reveal toggle's glyph — an eye, struck through while the password is on
 *  screen. The button carries the label; this is decoration. */
function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M1.5 12S5.7 4.8 12 4.8 22.5 12 22.5 12 18.3 19.2 12 19.2 1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3.2" />
      {crossed ? <path d="M4 20 20 4" /> : null}
    </svg>
  );
}

/** A date field: the platform's own picker, speaking days.
 *
 *  A date typed as text is a date mistyped as text, and on the shop floor that
 *  is a job booked into the wrong week. `type="date"` gets the iOS wheel on an
 *  iPad, the calendar on a desktop, and refuses a day that isn't one. The line
 *  underneath says the date back in the shop's own words with the year on it,
 *  because the field itself renders in the browser's locale format and "3/9"
 *  and "9/3" are different days.
 *
 *  The value is an `IsoDay` — "2026-09-03" — which is exactly what this input
 *  reads and writes, so there is no conversion in here at all. That is the
 *  point: a date field that translated between two representations is the one
 *  place a day can be lost, and it used to.
 *
 *  `null` is not set — an empty field — which is how an unscheduled job reads.
 */
export function DateField({
  name,
  value,
  onChange,
  min,
  hint,
  invalid = false,
  describedBy,
  autoFocus = false,
}: {
  name?: string;
  value: IsoDay | null;
  onChange: (day: IsoDay | null) => void;
  min?: IsoDay | null;
  hint?: string;
  invalid?: boolean;
  describedBy?: string;
  /** The form opened on this field — a job sheet's date clicked in place. */
  autoFocus?: boolean;
}) {
  return (
    <>
      <input
        type="date"
        name={name}
        className="input"
        autoComplete={NO_AUTOFILL}
        autoFocus={autoFocus}
        value={value ?? ''}
        min={min ?? undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        // The browser hands back "" for a cleared field and a real day
        // otherwise; anything else is not a day and is not taken.
        onChange={(e) => onChange(isDay(e.target.value) ? e.target.value : null)}
        style={{
          height: 48,
          fontSize: 16,
          ...(invalid ? { borderColor: 'var(--urgent)' } : null),
        }}
      />
      <span
        style={{
          fontSize: 12,
          color: 'var(--ink-muted)',
          fontFamily: value === null ? undefined : 'var(--font-data)',
        }}
      >
        {value === null ? (hint ?? 'Not set') : formatDay(value, { year: true })}
      </span>
    </>
  );
}

/** Modal shell. Clicking the backdrop closes; clicks inside do not bubble to it.
 *
 *  `zIndex` is here for the one dialog that opens over another: the unsaved-
 *  changes question is asked about the form underneath it, so it has to sit on
 *  top of it and take the backdrop click that would otherwise close it. */
export function Dialog({
  title,
  intro,
  width = 420,
  zIndex = 60,
  onClose,
  children,
  actions,
}: {
  title: string;
  intro?: string;
  width?: number;
  zIndex?: number;
  onClose: () => void;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="dialog-backdrop" style={{ zIndex }} onClick={onClose}>
      <div
        className="dialog blueprint"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface)', width: `min(${width}px, 100%)`, padding: 20 }}
      >
        <div className="dialog-title" style={{ fontSize: 20 }}>
          {title}
        </div>
        {intro ? (
          <p style={{ margin: '0 0 6px', fontSize: 13.5, color: 'var(--ink-muted)' }}>{intro}</p>
        ) : null}
        {children}
        {actions ? <div className="dialog-actions">{actions}</div> : null}
      </div>
    </div>
  );
}

/** Confirmation of what just happened, in the same words as the button that
 *  did it: "Move to Topcoat" produces "Moved to Topcoat". */
export function Toast({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 26,
        transform: 'translateX(-50%)',
        zIndex: 80,
        animation: 'o2p-rise .18s ease-out',
      }}
    >
      <div
        className="blueprint"
        style={{
          background: 'var(--inv-bg)',
          color: 'var(--inv-ink)',
          padding: '12px 18px',
          borderRadius: 14,
          fontSize: 14,
          maxWidth: 520,
        }}
      >
        {message}
      </div>
    </div>
  );
}

/** Back, as a mark rather than a row. Every detail screen used to spend a
 *  whole line on a "← Board" link; the arrow rides beside the heading instead —
 *  the job code, the customer's name — and the destination it can't draw is in
 *  its label, so the hover and the screen reader both get told where it goes. */
export function BackButton({
  to,
  onClick,
  style,
}: {
  to: string;
  onClick: () => void;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      className="btn btn-ghost btn-icon"
      title={`Back to ${to}`}
      aria-label={`Back to ${to}`}
      onClick={onClick}
      style={{ flex: 'none', ...style }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M15 5 8 12l7 7" />
      </svg>
    </button>
  );
}

/** Every empty state says what to do next, never "No data". */
export function Empty({ children, dashed = true }: { children: ReactNode; dashed?: boolean }) {
  return (
    <p
      style={{
        margin: 0,
        padding: 12,
        fontSize: 13,
        color: 'var(--ink-muted)',
        lineHeight: 1.45,
        border: dashed ? '1px dashed var(--line)' : 'none',
        borderRadius: 12,
      }}
    >
      {children}
    </p>
  );
}
